// src/admin/ClassTransferHistory.js - Quản lý và Hoàn tác Lịch Sử Chuyển Lớp Thiếu Nhi
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import { firestore } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  addDoc,
  onSnapshot,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import * as XLSX from 'xlsx';
import './ClassTransferHistory.css';

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (e) {
    return String(timestamp);
  }
};

const ClassTransferHistory = () => {
  const navigate = useNavigate();
  const { currentYearId: contextYearId, classMap } = useContext(StudentContext);
  const outletContext = useOutletContext() || {};
  const sidebarSelectedClass = outletContext.selectedClassForOutlet || 'all';

  const [currentYearId, setCurrentYearId] = useState('');
  const [currentYearName, setCurrentYearName] = useState('');
  const [transferLogs, setTransferLogs] = useState([]);
  const [classesList, setClassesList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Bộ lọc
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'ACTIVE' | 'REVERTED'
  const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });

  // State xử lý thao tác
  const [revertingId, setRevertingId] = useState(null);

  // Modal đổi sang lớp khác
  const [changeClassModal, setChangeClassModal] = useState({
    open: false,
    record: null,
    targetClassId: ''
  });
  const [isChangingClass, setIsChangingClass] = useState(false);

  // 1. Tải Niên Khóa & Danh mục Lớp
  useEffect(() => {
    const fetchYearAndClasses = async () => {
      setLoading(true);
      try {
        let yearId = contextYearId;
        let yearName = '';

        if (!yearId) {
          const yearQuery = query(collection(firestore, 'academic_years'), where('isCurrent', '==', true));
          const yearSnap = await getDocs(yearQuery);
          if (!yearSnap.empty) {
            yearId = yearSnap.docs[0].id;
            yearName = yearSnap.docs[0].data().name || yearId;
          } else {
            const allYearsSnap = await getDocs(query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc')));
            if (!allYearsSnap.empty) {
              yearId = allYearsSnap.docs[0].id;
              yearName = allYearsSnap.docs[0].data().name || yearId;
            }
          }
        }

        if (!yearId) {
          setLoading(false);
          return;
        }

        setCurrentYearId(yearId);
        setCurrentYearName(yearName);

        // Tải danh mục lớp
        const classesSnap = await getDocs(collection(firestore, `academic_years/${yearId}/classes`));
        const cList = classesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setClassesList(cList);
      } catch (err) {
        console.error('Lỗi tải thông tin niên khóa / lớp:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchYearAndClasses();
  }, [contextYearId]);

  // 2. Lắng nghe Realtime Lịch Sử Chuyển Lớp từ Firestore
  useEffect(() => {
    if (!currentYearId) return;

    const historyColRef = collection(firestore, `academic_years/${currentYearId}/class_transfer_history`);
    const q = query(historyColRef, orderBy('changedAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const logs = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));
      setTransferLogs(logs);
      setLoading(false);
    }, (err) => {
      console.error('Lỗi lắng nghe lịch sử chuyển lớp:', err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentYearId]);

  // 3. Lọc và Sắp Xếp dữ liệu
  const filteredLogs = useMemo(() => {
    return transferLogs.filter(log => {
      // (A) Lọc theo Sidebar chọn lớp
      if (sidebarSelectedClass && sidebarSelectedClass !== 'all') {
        const matchSidebar = log.fromClassId === sidebarSelectedClass || log.toClassId === sidebarSelectedClass;
        if (!matchSidebar) return false;
      }

      // (B) Lọc theo Trạng thái (Hiệu lực / Đã hoàn tác)
      if (statusFilter === 'ACTIVE' && log.isReverted) return false;
      if (statusFilter === 'REVERTED' && !log.isReverted) return false;

      // (C) Lọc theo Ngày chuyển
      if (dateFilter.startDate || dateFilter.endDate) {
        const logDate = log.changedAt?.toDate ? log.changedAt.toDate() : new Date(log.changedAt);
        if (dateFilter.startDate) {
          const start = new Date(dateFilter.startDate);
          start.setHours(0, 0, 0, 0);
          if (logDate < start) return false;
        }
        if (dateFilter.endDate) {
          const end = new Date(dateFilter.endDate);
          end.setHours(23, 59, 59, 999);
          if (logDate > end) return false;
        }
      }

      // (D) Lọc theo Từ khóa tìm kiếm
      if (searchTerm.trim()) {
        const qStr = searchTerm.toLowerCase().trim();
        const sId = (log.studentId || '').toLowerCase();
        const holyName = (log.holyName || '').toLowerCase();
        const fullName = (log.fullName || '').toLowerCase();
        const fromClass = (log.fromClassName || '').toLowerCase();
        const toClass = (log.toClassName || '').toLowerCase();
        const changer = (log.changedBy || '').toLowerCase();

        return (
          sId.includes(qStr) ||
          holyName.includes(qStr) ||
          fullName.includes(qStr) ||
          fromClass.includes(qStr) ||
          toClass.includes(qStr) ||
          changer.includes(qStr)
        );
      }

      return true;
    });
  }, [transferLogs, sidebarSelectedClass, statusFilter, dateFilter, searchTerm]);

  // Thống kê nhanh
  const stats = useMemo(() => {
    const total = filteredLogs.length;
    const active = filteredLogs.filter(l => !l.isReverted).length;
    const reverted = filteredLogs.filter(l => l.isReverted).length;
    return { total, active, reverted };
  }, [filteredLogs]);

  // ====================================================================
  // 4. THAO TÁC HOÀN TÁC (CHUYỂN TRỞ LẠI LỚP CŨ)
  // ====================================================================
  const handleRevertTransfer = async (record) => {
    if (record.isReverted) return;

    const confirmMsg = `XÁC NHẬN HOÀN TÁC CHUYỂN LỚP:\n\n• Thiếu nhi: ${record.holyName ? record.holyName + ' ' : ''}${record.fullName} (${record.studentId})\n• Chuyển từ lớp hiện tại [${record.toClassName}] TRỞ LẠI lớp cũ [${record.fromClassName}]?\n\nSau khi hoàn tác, phân lớp trong niên khóa sẽ được trả về lớp ban đầu.`;

    if (!window.confirm(confirmMsg)) return;

    setRevertingId(record.id);
    try {
      const adminName = localStorage.getItem('catechistFullName') || localStorage.getItem('username') || 'Admin';

      // 1. Cập nhật lại lớp trong enrollments của niên khóa
      const enrollmentRef = doc(firestore, `academic_years/${currentYearId}/enrollments`, record.studentId);
      await setDoc(enrollmentRef, {
        classId: record.fromClassId,
        gradeId: record.fromGradeId || ''
      }, { merge: true });

      // 2. Cập nhật trạng thái bản ghi lịch sử này thành đã hoàn tác
      const historyDocRef = doc(firestore, `academic_years/${currentYearId}/class_transfer_history`, record.id);
      await updateDoc(historyDocRef, {
        isReverted: true,
        revertedAt: Timestamp.now(),
        revertedBy: adminName
      });

      alert(`🎉 Đã hoàn tác thành công! Thiếu nhi ${record.fullName} đã được chuyển trở lại lớp "${record.fromClassName}".`);
    } catch (err) {
      console.error('Lỗi khi hoàn tác chuyển lớp:', err);
      alert('❌ Lỗi hệ thống: ' + err.message);
    } finally {
      setRevertingId(null);
    }
  };

  // ====================================================================
  // 5. THAO TÁC ĐỔI LỚP KHÁC (NẾU CHUYỂN NHẦM LỚP)
  // ====================================================================
  const handleOpenChangeClassModal = (record) => {
    setChangeClassModal({
      open: true,
      record,
      targetClassId: record.toClassId || ''
    });
  };

  const handleConfirmChangeClass = async (e) => {
    e.preventDefault();
    const { record, targetClassId } = changeClassModal;
    if (!record || !targetClassId) return;

    if (targetClassId === record.toClassId) {
      alert('Vui lòng chọn một lớp khác với lớp hiện tại!');
      return;
    }

    setIsChangingClass(true);
    try {
      const adminName = localStorage.getItem('catechistFullName') || localStorage.getItem('username') || 'Admin';
      const targetClassObj = classesList.find(c => c.id === targetClassId);
      const targetClassName = targetClassObj?.name || targetClassId;
      const targetGradeId = targetClassObj?.gradeId || '';

      // 1. Cập nhật enrollments
      const enrollmentRef = doc(firestore, `academic_years/${currentYearId}/enrollments`, record.studentId);
      await setDoc(enrollmentRef, {
        classId: targetClassId,
        gradeId: targetGradeId
      }, { merge: true });

      // 2. Đánh dấu bản ghi cũ thành đã chỉnh sửa/hoàn tác
      const oldDocRef = doc(firestore, `academic_years/${currentYearId}/class_transfer_history`, record.id);
      await updateDoc(oldDocRef, {
        isReverted: true,
        revertedAt: Timestamp.now(),
        revertedBy: `${adminName} (Đổi sang lớp ${targetClassName})`
      });

      // 3. Tạo một bản ghi mới chuẩn xác
      const historyColRef = collection(firestore, `academic_years/${currentYearId}/class_transfer_history`);
      await addDoc(historyColRef, {
        studentId: record.studentId,
        holyName: record.holyName || '',
        fullName: record.fullName,
        fromClassId: record.toClassId,
        fromClassName: record.toClassName,
        fromGradeId: record.toGradeId || '',
        toClassId: targetClassId,
        toClassName: targetClassName,
        toGradeId: targetGradeId,
        changedAt: Timestamp.now(),
        changedBy: adminName,
        isReverted: false,
        revertedAt: null,
        revertedBy: null,
        notes: `Điều chỉnh từ lần chuyển nhầm ngày ${formatTimestamp(record.changedAt)}`
      });

      alert(`🎉 Đã sửa và chuyển thiếu nhi ${record.fullName} sang lớp "${targetClassName}" thành công!`);
      setChangeClassModal({ open: false, record: null, targetClassId: '' });
    } catch (err) {
      console.error('Lỗi khi đổi lớp:', err);
      alert('❌ Lỗi hệ thống: ' + err.message);
    } finally {
      setIsChangingClass(false);
    }
  };

  // 6. Xuất File Excel
  const handleExportExcel = () => {
    if (filteredLogs.length === 0) {
      alert('Không có dữ liệu lịch sử để xuất Excel.');
      return;
    }

    const header = [
      'STT',
      'Thời Gian Chuyển',
      'Mã TN',
      'Tên Thánh',
      'Họ và Tên',
      'Lớp Cũ (Từ Lớp)',
      'Lớp Mới (Đến Lớp)',
      'Người Thực Hiện',
      'Trạng Thái',
      'Thời Gian Hoàn Tác',
      'Người Hoàn Tác'
    ];

    const data = filteredLogs.map((log, idx) => [
      idx + 1,
      formatTimestamp(log.changedAt),
      log.studentId,
      log.holyName || '',
      log.fullName,
      log.fromClassName,
      log.toClassName,
      log.changedBy || '',
      log.isReverted ? 'Đã hoàn tác' : 'Hiệu lực',
      log.revertedAt ? formatTimestamp(log.revertedAt) : '',
      log.revertedBy || ''
    ]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    XLSX.utils.book_append_sheet(wb, ws, 'LichSuChuyenLop');
    XLSX.writeFile(wb, `LichSuChuyenLop_${currentYearId || 'NienKhoa'}.xlsx`);
  };

  return (
    <div className="cth-container">
      {/* HEADER */}
      <div className="cth-header">
        <div>
          <h2 className="cth-title">📜 Lịch Sử Chuyển Lớp Thiếu Nhi</h2>
          <p className="cth-subtitle">
            Theo dõi nhật ký điều chuyển lớp của thiếu nhi trong Niên khóa <strong>{currentYearName || currentYearId}</strong>. Bạn có thể hoàn tác hoặc đổi lớp nếu chuyển nhầm.
          </p>
        </div>
        <div className="cth-header-actions">
          <button
            type="button"
            onClick={() => navigate('/admin-management/student-management')}
            className="cth-btn cth-btn-back"
          >
            👧 Về Quản Lý Thiếu Nhi
          </button>
          <button
            type="button"
            onClick={handleExportExcel}
            className="cth-btn cth-btn-excel"
          >
            📥 Xuất Excel ({filteredLogs.length})
          </button>
        </div>
      </div>

      {/* THẺ THỐNG KÊ NHANH */}
      <div className="cth-stats-grid">
        <div className="cth-stat-card blue">
          <span className="cth-stat-num">{stats.total}</span>
          <span className="cth-stat-lbl">Tổng Lượt Chuyển Lớp</span>
        </div>
        <div className="cth-stat-card green">
          <span className="cth-stat-num">{stats.active}</span>
          <span className="cth-stat-lbl">Đang Hiệu Lực</span>
        </div>
        <div className="cth-stat-card orange">
          <span className="cth-stat-num">{stats.reverted}</span>
          <span className="cth-stat-lbl">Đã Hoàn Tác (Trả Lại Lớp Cũ)</span>
        </div>
      </div>

      {/* THANH CÔNG CỤ BỘ LỌC */}
      <div className="cth-toolbar-card">
        <div className="cth-search-box">
          <input
            type="text"
            placeholder="🔎 Tìm kiếm Mã TN, Tên Thánh, Họ và Tên, Lớp, Người chuyển..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="cth-search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="cth-btn-clear" title="Xóa tìm kiếm">
              ✕
            </button>
          )}
        </div>

        <div className="cth-filter-group">
          <label className="cth-label">Trạng thái:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="cth-select"
          >
            <option value="ALL">-- Tất cả trạng thái --</option>
            <option value="ACTIVE">✓ Đang hiệu lực</option>
            <option value="REVERTED">↩️ Đã hoàn tác</option>
          </select>
        </div>

        <div className="cth-date-group">
          <label className="cth-label">Từ:</label>
          <input
            type="date"
            value={dateFilter.startDate}
            onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
            className="cth-date-input"
          />
          <label className="cth-label">Đến:</label>
          <input
            type="date"
            value={dateFilter.endDate}
            onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
            className="cth-date-input"
          />
          {(dateFilter.startDate || dateFilter.endDate) && (
            <button
              onClick={() => setDateFilter({ startDate: '', endDate: '' })}
              className="cth-btn-clear-date"
              title="Xóa lọc ngày"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* BẢNG LỊCH SỬ CHUYỂN LỚP */}
      <div className="cth-table-wrapper">
        <table className="cth-table">
          <thead>
            <tr>
              <th className="cth-text-center" style={{ width: '40px' }}>STT</th>
              <th style={{ width: '150px' }}>Thời Gian</th>
              <th style={{ width: '90px' }}>Mã TN</th>
              <th>Tên Thánh & Họ và Tên</th>
              <th>Lớp Ban Đầu (Từ Lớp)</th>
              <th className="cth-text-center" style={{ width: '30px' }}>➔</th>
              <th>Lớp Mới (Đến Lớp)</th>
              <th>Người Thực Hiện</th>
              <th className="cth-text-center" style={{ width: '130px' }}>Trạng Thái</th>
              <th className="cth-text-center" style={{ width: '180px' }}>Thao Tác Hoàn Tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="cth-empty-cell">
                  ⏳ Đang tải nhật ký chuyển lớp...
                </td>
              </tr>
            ) : filteredLogs.length === 0 ? (
              <tr>
                <td colSpan="10" className="cth-empty-cell">
                  Không tìm thấy lượt chuyển lớp nào phù hợp với bộ lọc hiện tại.
                </td>
              </tr>
            ) : (
              filteredLogs.map((log, idx) => {
                const isReverting = revertingId === log.id;
                return (
                  <tr key={log.id} className={log.isReverted ? 'cth-row-reverted' : ''}>
                    <td className="cth-text-center">{idx + 1}</td>
                    <td className="cth-time-cell">
                      {formatTimestamp(log.changedAt)}
                    </td>
                    <td className="cth-code-cell">{log.studentId}</td>
                    <td>
                      <span className="cth-holy-name">{log.holyName}</span>
                      <strong>{log.fullName}</strong>
                    </td>
                    <td>
                      <span className="cth-class-tag from">{log.fromClassName || 'Chưa xếp lớp'}</span>
                    </td>
                    <td className="cth-text-center cth-arrow-cell">➔</td>
                    <td>
                      <span className="cth-class-tag to">{log.toClassName || 'Chưa xếp lớp'}</span>
                    </td>
                    <td>
                      <span className="cth-user-badge">{log.changedBy || 'Admin'}</span>
                    </td>
                    <td className="cth-text-center">
                      {log.isReverted ? (
                        <div className="cth-reverted-info">
                          <span className="cth-badge-reverted">↩️ Đã hoàn tác</span>
                          <span className="cth-reverted-sub">
                            {formatTimestamp(log.revertedAt)} bởi {log.revertedBy}
                          </span>
                        </div>
                      ) : (
                        <span className="cth-badge-active">✓ Đang hiệu lực</span>
                      )}
                    </td>
                    <td className="cth-text-center">
                      <div className="cth-actions-group">
                        {/* NÚT HOÀN TÁC VỀ LỚP CŨ */}
                        <button
                          type="button"
                          onClick={() => handleRevertTransfer(log)}
                          disabled={log.isReverted || isReverting}
                          className="cth-btn-revert"
                          title={log.isReverted ? 'Lượt chuyển lớp này đã được hoàn tác' : `Hoàn tác đưa thiếu nhi về lại lớp ${log.fromClassName}`}
                        >
                          {isReverting ? '⏳ Đang hoàn tác...' : '↩️ Hoàn Tác'}
                        </button>

                        {/* NÚT CHUYỂN SANG LỚP KHÁC NẾU CHỌN NHẦM */}
                        <button
                          type="button"
                          onClick={() => handleOpenChangeClassModal(log)}
                          disabled={isReverting}
                          className="cth-btn-change-other"
                          title="Chuyển sang một lớp khác nếu đã chọn nhầm"
                        >
                          ✏️ Đổi Lớp
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL ĐỔI LỚP KHÁC KHI CHỌN NHẦM */}
      {changeClassModal.open && changeClassModal.record && (
        <div className="cth-modal-overlay">
          <div className="cth-modal-card">
            <h3 className="cth-modal-title">✏️ Điều Chỉnh Lớp Học (Sửa Chuyển Nhầm)</h3>
            <div className="cth-modal-info">
              <p><strong>Thiếu nhi:</strong> {changeClassModal.record.holyName} {changeClassModal.record.fullName} ({changeClassModal.record.studentId})</p>
              <p><strong>Lớp hiện tại (vừa chuyển):</strong> <span className="cth-class-tag to">{changeClassModal.record.toClassName}</span></p>
              <p><strong>Lớp ban đầu:</strong> <span className="cth-class-tag from">{changeClassModal.record.fromClassName}</span></p>
            </div>

            <form onSubmit={handleConfirmChangeClass}>
              <div className="cth-form-group">
                <label>Chọn lớp chính xác cần chuyển đến (*):</label>
                <select
                  value={changeClassModal.targetClassId}
                  onChange={(e) => setChangeClassModal({ ...changeClassModal, targetClassId: e.target.value })}
                  className="cth-modal-select"
                  required
                >
                  <option value="">-- Chọn Lớp Mới --</option>
                  {classesList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name || c.id}
                    </option>
                  ))}
                </select>
              </div>

              <div className="cth-modal-actions">
                <button
                  type="button"
                  onClick={() => setChangeClassModal({ open: false, record: null, targetClassId: '' })}
                  disabled={isChangingClass}
                  className="cth-btn cth-btn-cancel"
                >
                  Hủy Bỏ
                </button>
                <button
                  type="submit"
                  disabled={isChangingClass}
                  className="cth-btn cth-btn-save"
                >
                  {isChangingClass ? '⏳ Đang lưu...' : '💾 Xác Nhận Chuyển Lớp'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassTransferHistory;